import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Send } from "lucide-react";
import { SocialLinks, AGENCY_SOCIALS } from "@/components/SocialIcons";
import { toast } from "sonner";
import { ElephantLogo } from "@/components/ElephantLogo";
import { useLang, STR } from "@/i18n/translations";
import api from "@/lib/api";

export function Footer() {
  const { t } = useLang();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full bg-white/5 border border-gold/20 rounded px-3 py-2.5 text-sm text-cream focus:border-gold outline-none";

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", form);
      toast.success(t({ fr: "Inscription réussie !", en: "Subscribed!" }));
      setForm({ first_name: "", last_name: "", email: "" });
    } catch {
      toast.error(t({ fr: "Une erreur est survenue.", en: "Something went wrong." }));
    }
  };

  return (
    <footer className="relative overflow-hidden border-t border-gold/20 bg-obsidian pt-20 pb-10 grain">
      <div className="absolute -right-20 -bottom-16 opacity-[0.04] pointer-events-none">
        <ElephantLogo className="w-[420px] h-[420px]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid gap-12 lg:grid-cols-4">
        <div className="lg:col-span-1">
          <Link to="/" className="flex items-center gap-3">
            <ElephantLogo className="w-10 h-10" />
            <span className="font-serif text-2xl font-semibold">Sily <span className="text-gold">Agency</span></span>
          </Link>
          <p className="mt-5 text-sm text-cream/60 leading-relaxed max-w-xs font-serif italic text-lg">
            « {t(STR.common.slogan)} »
          </p>
          <p className="eyebrow mt-6 mb-3">{t({ fr: "Suivez-nous", en: "Follow us" })}</p>
          <SocialLinks links={AGENCY_SOCIALS} owner="Sily Agency" prefix="footer-social"
            itemClass="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-cream/80 hover:text-obsidian hover:bg-gold hover:border-gold transition-colors" />
        </div>

        <div>
          <h4 className="eyebrow mb-5">Navigation</h4>
          <ul className="space-y-3 text-sm text-cream/70">
            {["home", "services", "artists", "events"].map((k) => (
              <li key={k}><Link to={k === "home" ? "/" : `/${k === "artists" ? "artistes" : k === "events" ? "evenements" : k}`} className="hover:text-gold transition-colors">{t(STR.nav[k])}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="eyebrow mb-5">{t(STR.nav.contact)}</h4>
          <ul className="space-y-3 text-sm text-cream/70">
            <li><a href="mailto:silyagency@gmail.com" className="hover:text-gold transition-colors">silyagency@gmail.com</a></li>
            <li><a href="tel:+33758490459" className="hover:text-gold transition-colors">+33 (0)7 58 49 04 59</a></li>
            <li className="text-cream/50">1 rue Henri Gonnard, 42000 Saint-Étienne</li>
            <li><Link to="/booking" className="hover:text-gold transition-colors">{t(STR.common.requestBooking)}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="eyebrow mb-5">Newsletter</h4>
          <p className="text-sm text-cream/60 mb-4">{t({ fr: "Recevez nos actualités.", en: "Get our news." })}</p>
          <form onSubmit={subscribe} className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input required value={form.first_name} onChange={set("first_name")} placeholder={t({ fr: "Prénom", en: "First name" })} data-testid="newsletter-first-name" className={inputCls} />
              <input required value={form.last_name} onChange={set("last_name")} placeholder={t({ fr: "Nom", en: "Last name" })} data-testid="newsletter-last-name" className={inputCls} />
            </div>
            <div className="flex gap-2">
            <input
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              placeholder="Email"
              data-testid="newsletter-input"
              className={inputCls}
            />
            <button type="submit" data-testid="newsletter-submit" className="btn-gold rounded px-4 flex items-center justify-center">
              <Send className="w-4 h-4" />
            </button>
            </div>
          </form>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-xs text-cream/40">
        <span>© {new Date().getFullYear()} Sily Agency. {t({ fr: "Tous droits réservés.", en: "All rights reserved." })}</span>
        <span className="text-center">SIREN 991 829 540 — R.C.S. Saint-Étienne</span>
        <Link to="/admin/login" className="hover:text-gold transition-colors" data-testid="footer-admin-link">Admin</Link>
      </div>
    </footer>
  );
}
